import { useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  CreateBusinessPartnerRequestDto,
  findContactsByCreatedByIdQueryKey,
  FindContactsByCreatedByIdResponseDto,
  useUpdateBusinessPartner,
} from "~/gen/api"
import {
  organizationRequestDtoCountryEnum as organizationCountryEnum,
  type OrganizationRequestDtoCountryEnumKey,
} from "~/gen/api/types/OrganizationRequestDto.ts"
import type { UpdateBusinessPartnerRequestDto } from "~/gen/api/types/UpdateBusinessPartnerRequestDto.ts"
import type { BusinessPartnerRepositoryAddressSnapshot } from "~/pages/Admin/components/form.types"
import { SupplierFormValues } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/columns"
import { toCreateBusinessPartnerRepositoryRecordPayload } from "~/pages/Admin/components/Fields/BusinessPartnerRepositoryAutocomplete/utils"
import { useCreateContactMutation } from "~/pages/SupplierPortal/Contacts/hooks/useCreateContactMutation"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

const isOrganizationRequestDtoCountryKey = (
  value: string | null | undefined
): value is OrganizationRequestDtoCountryEnumKey => {
  return (
    typeof value === "string" &&
    (Object.values(organizationCountryEnum) as string[]).includes(value)
  )
}

const organizationCountryFromApi = (
  value: string | null | undefined
): OrganizationRequestDtoCountryEnumKey => {
  if (isOrganizationRequestDtoCountryKey(value)) {
    return value
  }
  return organizationCountryEnum.FR
}

const hasPersistedContactId = (
  form: SupplierFormValues
): form is UpdateBusinessPartnerRequestDto & { contactId: string } => {
  return "contactId" in form && form.contactId !== ""
}

type UseContactFormProps = {
  initialValues?: SupplierFormValues
  onContactCreated?: (contactId: string) => void
  organizationId: string
}
export const contactToSupplierMapping = (
  contact: FindContactsByCreatedByIdResponseDto
): SupplierFormValues => {
  return {
    contactId: contact.id,
    supplierId: contact.user.id.toString(),
    name: contact.user.name,
    email: contact.user.email,
    preferedLangageIso6391: contact.user.preferedLangageIso6391,
    organization: {
      businessRelationship: contact.user.organization.businessRelationship,
      name: contact.user.organization.name,
      street: contact.user.organization.street ?? "",
      city: contact.user.organization.city ?? "",
      postalCode: contact.user.organization.postalCode ?? "",
      country: organizationCountryFromApi(contact.user.organization.country),
      registrationId: contact.user.organization.registrationId,
      coordinates: contact.user.organization.coordinates ?? null,
    },
  }
}

const defaultContact: SupplierFormValues = {
  contactId: "",
  supplierId: "",
  name: "",
  email: "",
  preferedLangageIso6391: "EN",
  organization: {
    businessRelationship: {
      isActive: true,
      supplierCode: null,
      type: null,
    },
    name: "",
    street: "",
    city: "",
    postalCode: "",
    country: organizationCountryEnum.FR,
    registrationId: [],
    coordinates: null,
  },
}

const useContactForm = ({
  initialValues,
  onContactCreated,
  organizationId,
}: UseContactFormProps) => {
  const { t } = useTranslation("traceability")

  const [contact, setContact] = useState<SupplierFormValues>(
    initialValues ?? defaultContact
  )
  const contactId =
    "contactId" in contact && contact.contactId !== "" ? contact.contactId : ""

  const { mutate: createContactMutate, isPending: isCreating } =
    useCreateContactMutation(organizationId, {
      onSuccess: (data) => {
        onContactCreated?.(data)
        setContact((oldContact) => ({
          ...oldContact,
          contactId: data.toString(),
        }))
      },
    })

  const createContact = (
    values: CreateBusinessPartnerRequestDto,
    options?: {
      repositoryRecord?: BusinessPartnerRepositoryAddressSnapshot
      duplicateCandidateId?: string
    }
  ) => {
    const payload: CreateBusinessPartnerRequestDto = {
      ...values,
    }
    if (options?.repositoryRecord) {
      payload.repositoryRecord =
        toCreateBusinessPartnerRepositoryRecordPayload(
          options.repositoryRecord
        )
    }
    const trimmedDuplicateId = options?.duplicateCandidateId?.trim()
    if (
      trimmedDuplicateId !== undefined &&
      trimmedDuplicateId !== null &&
      trimmedDuplicateId !== ""
    ) {
      payload.duplicateCandidateId = trimmedDuplicateId
    }
    createContactMutate(payload)
  }

  const { mutate: updateContactMutate, isPending: isUpdating } =
    useUpdateBusinessPartner({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: findContactsByCreatedByIdQueryKey(organizationId),
          })
          await queryClient.invalidateQueries({
            queryKey: ["tags"],
          })
          showSuccessNotification({
            message: t("contactModal.messages.update.success", {
              email: contact.email,
            }),
            title: t("contactModal.messages.update.success", {
              email: contact.email,
            }),
          })
        },
      },
    })

  const updateContact = (values: UpdateBusinessPartnerRequestDto) => {
    updateContactMutate({
      id: contactId,
      data: values,
    })
  }

  const handleContact = (
    values?: SupplierFormValues,
    options?: {
      repositoryRecord?: BusinessPartnerRepositoryAddressSnapshot
      duplicateCandidateId?: string
    }
  ) => {
    const snapshot: SupplierFormValues = values ? { ...contact, ...values } : contact

    if (hasPersistedContactId(snapshot)) {
      updateContact({
        supplierId: snapshot.supplierId,
        name: snapshot.name,
        email: snapshot.email,
        preferedLangageIso6391: snapshot.preferedLangageIso6391,
        organization: snapshot.organization,
      })
    } else {
      createContact(
        {
          name: snapshot.name,
          email: snapshot.email,
          preferedLangageIso6391: snapshot.preferedLangageIso6391,
          organization: snapshot.organization,
        },
        {
          repositoryRecord: options?.repositoryRecord,
          duplicateCandidateId: options?.duplicateCandidateId,
        }
      )
    }
  }

  const reset = () => {
    setContact(defaultContact)
  }

  return {
    contact,
    setContact,
    contactId,
    handleContact,
    reset,
    isCreating,
    isUpdating,
  }
}

export default useContactForm
